import React from 'react';
import PropTypes from 'prop-types';
import fastDeepEqual from 'fast-deep-equal';
/* rules */
import rules from './validation-rules';

export const ValidationContext = React.createContext();

class ValidationForm extends React.PureComponent {
    constructor(props) {
        super(props);
        this.components = {};

        this.state = {
            showErrors: false,
            errors: {},
            staticErrors: {},
            warnings: {},
            submitDisabled: false
        };
    }

    getContext() {
        return {
            components: this.components,
            errors: { ...this.state.errors, ...this.getStaticErrors() },
            warnings: this.getWarnings(),
            register: this.register,
            resetValidation: this.resetValidation,
            showErrors: this.state.showErrors,
            submitDisabled: this.state.submitDisabled,
            unregister: this.unregister,
            validateState: this.validateState
        };
    }

    submit = (form) => {
        const errors = this.validateAll();
        if (!Object.keys(errors).length) {
            if (this.props.onSubmit) {
                this.props.onSubmit(form, this.enableSubmitButton);
            } else {
                form.submit();
            }
        } else {
            this.enableSubmitButton();
        }
    };

    enableSubmitButton = () => this.setState({ submitDisabled: false });

    submitForm = (form) => {
        if (this.props.resolveBeforeSubmit) {
            this.props.resolveBeforeSubmit.then(() => {
                this.submit(form);
            });
        } else {
            this.submit(form);
        }
    };

    handleSubmit = (e) => {
        e.preventDefault();
        const form = e.target;
        if (this.props.disableOnSubmit) {
            this.setState({ submitDisabled: true }, () => {
                this.submitForm(form);
            });
        } else {
            this.submitForm(form);
        }
    };

    getStaticErrors = () => {
        const staticErrors = {};
        Object.keys(this.state.staticErrors).forEach((key) => {
            if (this.components[key]) {
                staticErrors[key] = this.state.staticErrors[key];
            }
        });
        return staticErrors;
    };

    getWarnings = () => {
        const warnings = {};
        Object.keys(this.state.warnings).forEach((key) => {
            if (this.components[key]) {
                warnings[key] = this.state.warnings[key];
            }
        });
        return warnings;
    };

    getErrors = () => {
        const errors = {};

        Object.keys(this.components).forEach((name) => {
            const component = this.components[name];
            const validations = component.props.validations;
            const validate = typeof component.state.validate === 'boolean' ? component.state.validate : true;

            if (validate && validations) {
                Object.keys(validations).forEach((key) => {
                    const validationRule = rules[key] || rules.custom;
                    if (!validationRule.rule(component.state.value, validations[key], this.components, name)) {
                        errors[name] = errors[name] || [];
                        errors[name].push(key);
                    }
                });
            }
        });

        return errors;
    };

    register = component => {
        if (process.env.NODE_ENV === 'development') {
            /* eslint-disable no-console */
            if (!component.props.children.props.name) {
                console.error(component.props.children, 'must have a valid "name" prop');
            }
            /* eslint-enable no-console */
        }
        this.components[component.props.children.props.name] = component;
        this.validateState(true);
    };

    unregister = componentName => {
        delete this.components[componentName];
        this.validateState();
        // this.setState((prevState) => {
        //     const errors = { ...prevState.errors };
        //     delete errors[component.props.children.props.name];
        //     return { ...prevState, errors };
        // });
    };

    validateState = (forceValidate = false) => {
        if (this.state.showErrors) {
            const errors = this.getErrors();
            if (forceValidate || !fastDeepEqual(this.state.errors, errors)) {
                this.setState({ errors });
            }
        }
    };

    validateAll = () => {
        const errors = this.getErrors();
        if (this.props.clearErrorsOnSubmit) {
            this.setState(prevState => ({
                ...prevState,
                showErrors: true,
                errors,
                staticErrors: {},
                warnings: {}
            }), this.props.onInvalid);
            return errors;
        }
        this.setState(prevState => ({
            ...prevState,
            showErrors: true,
            errors,
            warnings: {}
        }), this.props.onInvalid);
        return { ...errors, ...this.getStaticErrors() };
    };

    showError = (name, error) => {
        this.setState((prevState) => {
            const staticErrors = { ...prevState.staticErrors };
            staticErrors[name] = [error];
            return { ...prevState, staticErrors };
        });
    };

    hideError = name => {
        this.setState((prevState) => {
            const staticErrors = { ...prevState.staticErrors };
            if (staticErrors[name]) {
                delete staticErrors[name];
            }
            return { ...prevState, staticErrors };
        });
    };

    hideAllErrors = callback => {
        this.setState(prevState => ({ ...prevState, staticErrors: {} }), callback);
    };

    showWarning = (name, warning) => {
        this.setState((prevState) => {
            const warnings = { ...prevState.warnings };
            warnings[name] = [warning];
            return { ...prevState, warnings };
        });
    };

    hideWarning = name => {
        this.setState((prevState) => {
            const warnings = { ...prevState.warnings };
            if (warnings[name]) {
                delete warnings[name];
            }
            return { ...prevState, warnings };
        });
    };

    hideAllWarnings = callback => {
        this.setState(prevState => ({ ...prevState, warnings: {} }), callback);
    };

    resetValidation = () => {
        this.setState({ errors: {}, staticErrors: {}, warnings: {}, showErrors: false });
    };

    render() {
        let classNames = 'sten-validation-form';
        if (this.props.className) {
            classNames += ` ${this.props.className}`;
        }
        return (
            <ValidationContext.Provider value={this.getContext()}>
                <form
                    className={classNames}
                    onSubmit={this.handleSubmit}
                    method={this.props.method}
                    name={this.props.name}
                    action={this.props.action}
                >
                    {this.props.children}
                </form>
            </ValidationContext.Provider>
        );
    }
}

ValidationForm.propTypes = {
    action: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    clearErrorsOnSubmit: PropTypes.bool,
    disableOnSubmit: PropTypes.bool,
    method: PropTypes.string.isRequired,
    name: PropTypes.string,
    onInvalid: PropTypes.func,
    onSubmit: PropTypes.func,
    resolveBeforeSubmit: PropTypes.objectOf(PropTypes.any)
};

ValidationForm.defaultProps = {
    action: null,
    children: null,
    className: '',
    clearErrorsOnSubmit: false,
    disableOnSubmit: true,
    name: '',
    onInvalid: undefined,
    onSubmit: undefined,
    resolveBeforeSubmit: undefined
};

export default ValidationForm;
